import { StaffShiftData } from './SimpleShiftBoard';
import { ExtendedStaffShiftData, convertToExtended, convertToBasic } from '../../types/extended-shift';
import { format, parse, isWithinInterval } from 'date-fns';
import { DataService } from '../../services/dataService';

/**
 * サイトコントローラー用シフトデータ管理クラス
 * データの保存・取得・操作を一元管理
 */
export class ShiftDataManager {

  /**
   * 全シフトデータを取得（基本版）
   */
  static async getAllShifts(): Promise<StaffShiftData[]> {
    try {
      return await DataService.getStaffShifts();
    } catch (error) {
      console.error('シフトデータの読み込みに失敗:', error);
      return [];
    }
  }

  /**
   * 全拡張シフトデータを取得
   */
  static async getAllExtendedShifts(): Promise<ExtendedStaffShiftData[]> {
    try {
      const extendedData = await DataService.getExtendedStaffShifts();
      if (extendedData && extendedData.length > 0) {
        return extendedData;
      }

      // 拡張データがない場合は基本データから変換
      const basicData = await this.getAllShifts();
      const convertedData = basicData.map(convertToExtended);
      
      // 変換したデータを拡張ストレージに保存
      if (convertedData.length > 0) {
        await this.saveAllExtendedShifts(convertedData);
      }

      return convertedData;
    } catch (error) {
      console.error('拡張シフトデータの読み込みに失敗:', error);
      return [];
    }
  }

  /**
   * シフトデータを保存（基本版）
   */
  static async saveAllShifts(shifts: StaffShiftData[]): Promise<boolean> {
    try {
      await DataService.saveStaffShifts(shifts);
      return true;
    } catch (error) {
      console.error('シフトデータの保存に失敗:', error);
      return false;
    }
  }

  /**
   * 拡張シフトデータを保存
   */
  static async saveAllExtendedShifts(shifts: ExtendedStaffShiftData[]): Promise<boolean> {
    try {
      await DataService.saveExtendedStaffShifts(shifts);
      
      // 後方互換性のため基本データも同期保存
      const basicShifts = shifts.map(convertToBasic);
      await this.saveAllShifts(basicShifts);
      
      return true;
    } catch (error) {
      console.error('拡張シフトデータの保存に失敗:', error);
      return false;
    }
  }

  /**
   * 特定スタッフの指定日シフトを取得（基本版）
   */
  static async getStaffSchedule(staffId: string, date: Date): Promise<StaffShiftData | null> {
    const dateStr = format(date, 'yyyy-MM-dd');
    const shifts = await this.getAllShifts();
    return shifts.find(s => s.staffId === staffId && s.date === dateStr) || null;
  }

  /**
   * 特定スタッフの指定日拡張シフトを取得
   */
  static async getExtendedStaffSchedule(staffId: string, date: Date): Promise<ExtendedStaffShiftData | null> {
    const dateStr = format(date, 'yyyy-MM-dd');
    const shifts = await this.getAllExtendedShifts();
    return shifts.find(s => s.staffId === staffId && s.date === dateStr) || null;
  }

  /**
   * 特定日の全スタッフシフトを取得
   */
  static async getAllSchedulesForDate(date: Date): Promise<StaffShiftData[]> {
    const dateStr = format(date, 'yyyy-MM-dd');
    const shifts = await this.getAllShifts();
    return shifts.filter(s => s.date === dateStr);
  }

  /**
   * スタッフの勤務情報を更新（基本版）
   */
  static async updateStaffSchedule(data: Omit<StaffShiftData, 'createdAt' | 'updatedAt'>): Promise<boolean> {
    try {
      const now = new Date().toISOString();
      
      if (data.id) {
        // 既存データを更新
        await DataService.updateStaffShift(data.id, {
          ...data,
          updatedAt: now,
        });
      } else {
        // 新規データを追加
        const { ...addData } = data as StaffShiftData;
        await DataService.addStaffShift(addData);
      }
      
      return true;
    } catch (error) {
      console.error('シフトデータの更新に失敗:', error);
      return false;
    }
  }

  /**
   * 拡張スタッフ勤務情報を更新
   */
  static async updateExtendedStaffSchedule(data: ExtendedStaffShiftData): Promise<boolean> {
    try {
      const shifts = await this.getAllExtendedShifts();
      const existingIndex = shifts.findIndex(s => s.id === data.id);
      
      if (existingIndex >= 0) {
        // 既存データを更新
        shifts[existingIndex] = {
          ...data,
          updatedAt: new Date().toISOString(),
        };
      } else {
        // 新規データを追加
        shifts.push(data);
      }
      
      return await this.saveAllExtendedShifts(shifts);
    } catch (error) {
      console.error('拡張シフトデータの更新に失敗:', error);
      return false;
    }
  }

  /**
   * スタッフが指定日時に勤務中かどうか判定
   */
  static async isStaffWorking(staffId: string, date: Date, time?: string): Promise<boolean> {
    const schedule = await this.getStaffSchedule(staffId, date);
    
    if (!schedule || !schedule.workingHours) {
      return false;
    }

    if (!time) {
      // 時刻指定がない場合は、その日に勤務予定があるかどうかを返す
      return true;
    }

    try {
      // 指定時刻が勤務時間内かチェック
      const targetTime = parse(time, 'HH:mm', date);
      const startTime = parse(schedule.workingHours.start, 'HH:mm', date);
      const endTime = parse(schedule.workingHours.end, 'HH:mm', date);
      
      return isWithinInterval(targetTime, { start: startTime, end: endTime });
    } catch (error) {
      console.error('時刻判定でエラー:', error);
      return false;
    }
  }

  /**
   * 指定期間の勤務中スタッフを取得
   */
  static async getWorkingStaffForDateRange(startDate: Date, endDate: Date): Promise<{
    date: string;
    workingStaff: { staffId: string; workingHours: { start: string; end: string } }[];
  }[]> {
    const shifts = await this.getAllShifts();
    const result: { [date: string]: { staffId: string; workingHours: { start: string; end: string } }[] } = {};
    
    shifts
      .filter(s => {
        const shiftDate = new Date(s.date);
        return shiftDate >= startDate && shiftDate <= endDate && s.workingHours;
      })
      .forEach(shift => {
        const dateKey = shift.date;
        if (!result[dateKey]) {
          result[dateKey] = [];
        }
        if (shift.workingHours) {
          result[dateKey].push({
            staffId: shift.staffId,
            workingHours: shift.workingHours,
          });
        }
      });

    return Object.entries(result).map(([date, workingStaff]) => ({
      date,
      workingStaff,
    }));
  }

  /**
   * スタッフの週間勤務時間を計算
   */
  static async calculateWeeklyWorkingHours(staffId: string, weekStart: Date): Promise<number> {
    const shifts = await this.getAllShifts();
    let totalHours = 0;

    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      const dateStr = format(date, 'yyyy-MM-dd');
      
      const shift = shifts.find(s => s.staffId === staffId && s.date === dateStr);
      if (shift?.workingHours) {
        try {
          const start = parse(shift.workingHours.start, 'HH:mm', date);
          const end = parse(shift.workingHours.end, 'HH:mm', date);
          const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
          totalHours += hours;
        } catch (error) {
          console.warn('勤務時間計算でエラー:', error);
        }
      }
    }

    return Math.round(totalHours * 10) / 10; // 小数点第1位まで
  }

  /**
   * データをエクスポート（バックアップ用）
   */
  static async exportData(): Promise<string> {
    const shifts = await this.getAllShifts();
    return JSON.stringify({
      exportDate: new Date().toISOString(),
      version: '1.0',
      data: shifts,
    }, null, 2);
  }

  /**
   * データをインポート（復元用）
   */
  static async importData(jsonData: string): Promise<boolean> {
    try {
      const parsed = JSON.parse(jsonData);
      if (parsed.data && Array.isArray(parsed.data)) {
        return await this.saveAllShifts(parsed.data);
      }
      return false;
    } catch (error) {
      console.error('データインポートに失敗:', error);
      return false;
    }
  }

  /**
   * 統計情報を取得
   */
  static async getStatistics(): Promise<{
    totalShifts: number;
    activeStaff: number;
    averageWorkingHours: number;
    lastUpdated: string | null;
  }> {
    const shifts = await this.getAllShifts();
    const workingShifts = shifts.filter(s => s.workingHours);
    
    const uniqueStaff = new Set(shifts.map(s => s.staffId));
    
    let totalHours = 0;
    workingShifts.forEach(shift => {
      if (shift.workingHours) {
        try {
          const date = new Date(shift.date);
          const start = parse(shift.workingHours.start, 'HH:mm', date);
          const end = parse(shift.workingHours.end, 'HH:mm', date);
          totalHours += (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        } catch (error) {
          // エラーは無視
        }
      }
    });

    const lastUpdated = shifts.length > 0 
      ? shifts.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0].updatedAt
      : null;

    return {
      totalShifts: shifts.length,
      activeStaff: uniqueStaff.size,
      averageWorkingHours: workingShifts.length > 0 ? Math.round((totalHours / workingShifts.length) * 10) / 10 : 0,
      lastUpdated,
    };
  }
}

/**
 * サイトコントローラー用のヘルパー関数
 */
export const ShiftHelpers = {
  /**
   * 現在時刻で勤務中のスタッフIDリストを取得
   */
  async getCurrentWorkingStaff(): Promise<string[]> {
    const now = new Date();
    const currentTime = format(now, 'HH:mm');
    const todayShifts = await ShiftDataManager.getAllSchedulesForDate(now);
    
    const workingStaff = [];
    for (const shift of todayShifts) {
      if (shift.workingHours && await ShiftDataManager.isStaffWorking(shift.staffId, now, currentTime)) {
        workingStaff.push(shift.staffId);
      }
    }
    return workingStaff;
  },

  /**
   * 指定時刻に利用可能なスタッフ数を取得
   */
  async getAvailableStaffCount(date: Date, time: string): Promise<number> {
    const shifts = await ShiftDataManager.getAllSchedulesForDate(date);
    let count = 0;
    for (const shift of shifts) {
      if (await ShiftDataManager.isStaffWorking(shift.staffId, date, time)) {
        count++;
      }
    }
    return count;
  },

  /**
   * 週間勤務スケジュール概要を取得
   */
  async getWeeklyScheduleOverview(weekStart: Date) {
    const overview = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      const shifts = await ShiftDataManager.getAllSchedulesForDate(date);
      const workingCount = shifts.filter(s => s.workingHours).length;
      
      overview.push({
        date: format(date, 'yyyy-MM-dd'),
        workingStaffCount: workingCount,
        totalScheduled: shifts.length,
      });
    }
    return overview;
  },
};